/**
 * Dependencies.
 */
import { orderBy } from 'lodash-es';
import Vue from 'vue';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';

import { ServicePointOrderModel } from '@/api/service-point/order/order.model';
import { ServicePointModel } from '@/api/service-point/service-point/service-point.model';
import {
  IOrdersTotal,
  servicePointService
} from '@/api/service-point/service-point/service-point.service';
import { usersService } from '@/api/service-point/user/user.service';
import OrderItemSummary from '@/modules/service-point/components/order/item-summary/item-summary.component.vue';
import CountSummaryComponent from '@/modules/service-point/components/summaries/count-summary.component.vue';
import orderState from '@/modules/service-point/constants/order.constant';
import { servicePointStore } from '@/modules/service-point/store';
import { IUser } from '@/ui/components/user-menu/user-menu.types';

/**
 * Component.
 */
@Component({
  components: {
    'count-summary': CountSummaryComponent,
    'order-item-summary': OrderItemSummary
  },
  name: 'service-point-home-scene'
})
export default class extends Vue {
  /**
   * Property `canShowReceiveButton`.
   */
  canShowReceiveButton = false;

  /**
   * Property `isLoadingFirstOrders`.
   */
  isLoadingFirstOrders = false;

  /**
   * Property `isLoadingPendingOrders`.
   */
  isLoadingPendingOrders = false;

  /**
   * Property `isLoadingAllOrders`.
   */
  isLoadingAllOrders = false;

  /**
   * Property `isBottomSheetVisible`.
   */
  isBottomSheetVisible = true;

  /**
   * Property `isBottomSheetExpanded`.
   */
  isBottomSheetExpanded = false;

  /**
   * Property `ordersHeight`.
   */
  ordersHeight = null;

  /**
   * Property `deliveries`.
   */
  deliveries: ServicePointOrderModel[] = [];

  /**
   * Property `pendingOrders`.
   */
  pendingOrders: ServicePointOrderModel[] = [];

  /**
   * Property `ordersInTransit`.
   */
  ordersInTransit: ServicePointOrderModel[] = [];

  /**
   * Property `allOrders`.
   */
  allOrders: ServicePointOrderModel[] = [];

  /**
   * Property `allOrdersPage`.
   */
  allOrdersPage = 1;

  /**
   * Property `assignedPoints`.
   */
  assignedPoints = 0;

  /**
   * Property `ordersTypes`.
   */
  ordersTypes = {
    ALL_ORDERS: 'ALL_ORDERS',
    IN_TRANSIT: 'IN_TRANSIT',
    PENDING_ORDERS: 'PENDING_ORDERS'
  };

  /**
   * Property `selectedTab`.
   */
  selectedTab: string = this.ordersTypes.PENDING_ORDERS;

  /**
   * Property `scrollObserver`.
   */
  scrollObserver = null;

  /**
   * Property `ordersTotals`.
   */
  ordersTotals: IOrdersTotal = null;

  /**
   * Reference `ordersScrollEndElement`.
   */
  @Ref()
  ordersScrollEndElement: HTMLElement;

  /**
   * Property `servicePoint`.
   */
  @Prop({ type: Object })
  servicePoint: ServicePointModel;

  /**
   * Property `user`.
   */
  @Prop({ type: Object })
  user: IUser;

  /**
   * Getter `canShowReturnsButton`.
   */
  get canShowReturnsButton() {
    const { configs } = this.servicePoint || {};
    const { ServicePointAcceptCollects } = configs || {};
    const canAcceptCollects = ServicePointAcceptCollects === 'true';

    return canAcceptCollects;
  }

  /**
   * Getter `greeting`.
   */
  get greeting() {
    const { user } = this || {};
    const { name } = user || {};

    return this.i18n('SERVICE_POINT.SENTENCES.HI', false, 'upperFirst', {
      name
    }).replace(' ,', ',');
  }

  /**
   * Getter `welcome`.
   */
  get welcome() {
    const { assignedPoints = 0 } = this || {};

    let welcomeMessage = this.i18n('SERVICE_POINT.SENTENCES.WELCOME');

    if (assignedPoints) {
      welcomeMessage = this.i18n(
        'SERVICE_POINT.SENTENCES.WELCOME_WITH_PROZIS_POINTS',
        { data: { prozisPoints: assignedPoints } }
      );
    }

    return welcomeMessage;
  }

  /**
   * Getter isPendingOrdersTabSelected`.
   */
  get isPendingOrdersTabSelected() {
    return this.selectedTab === this.ordersTypes.PENDING_ORDERS;
  }

  /**
   * Getter isInTransitOrdersTabSelected`.
   */

  get isInTransitOrdersTabSelected() {
    return this.selectedTab === this.ordersTypes.IN_TRANSIT;
  }

  /**
   * Getter `isAllOrdersTabSelected`.
   */
  get isAllOrdersTabSelected() {
    return this.selectedTab === this.ordersTypes.ALL_ORDERS;
  }

  /**
   * Getter `totalOfOrders`.
   */
  get totalOfOrders() {
    let total = 0;

    const {
      collectsToReceive = 0,
      ordersCanceled = 0,
      ordersFinished = 0,
      ordersInServicePoint = 0,
      ordersToReceive = 0
    } = this.ordersTotals || {};
    const ordersInTransit = ordersToReceive + collectsToReceive;
    const ordersProcessed = ordersFinished + ordersCanceled;

    switch (this.selectedTab) {
      case this.ordersTypes.PENDING_ORDERS:
        total = ordersInServicePoint;
        break;

      case this.ordersTypes.IN_TRANSIT:
        total = ordersInTransit;
        break;

      case this.ordersTypes.ALL_ORDERS:
        total = ordersInServicePoint + ordersInTransit + ordersProcessed;
        break;
    }

    return total;
  }

  /**
   * Getter `totalOfPackages`.
   */
  get totalOfPackages() {
    let total = 0;

    const {
      collectPackagesToReceive = 0,
      packagesFinished = 0,
      packagesInServicePoint = 0,
      packagesToReceive = 0
    } = this.ordersTotals || {};
    const packagesInTransit = packagesToReceive + collectPackagesToReceive;

    switch (this.selectedTab) {
      case this.ordersTypes.PENDING_ORDERS:
        total = packagesInServicePoint;
        break;

      case this.ordersTypes.IN_TRANSIT:
        total = packagesInTransit;
        break;

      case this.ordersTypes.ALL_ORDERS:
        total = packagesInServicePoint + packagesInTransit + packagesFinished;
        break;
    }

    return total;
  }

  /**
   * Getter `filteredOrders`.
   */
  get filteredOrders() {
    const deliveries = orderBy(
      this.deliveries || [],
      ['expectedDeliveryDate'],
      ['desc']
    );

    let orders = [];

    switch (this.selectedTab) {
      case this.ordersTypes.ALL_ORDERS:
        orders = [...deliveries, ...(this.allOrders || [])];
        break;

      case this.ordersTypes.IN_TRANSIT:
        orders = [...deliveries, ...(this.ordersInTransit || [])];
        break;

      case this.ordersTypes.PENDING_ORDERS:
        orders = [...(this.pendingOrders || [])];
        break;
    }

    orders = [...orderBy(orders, ['statusDate'], ['desc'])];

    return orders;
  }

  /**
   * Lifecycle created hook.
   */
  created() {
    this.handleInitialize();
  }

  /**
   * Lifecycle mounted hook.
   */
  mounted() {
    this.handleGetServicePointUser();
  }

  /**
   * Handle initialize.
   */
  async handleInitialize() {
    this.isLoadingFirstOrders = true;

    await this.handleGetOrdersTotals();
    await this.handleGetDeliveries();
    await this.handleGetPendingOrders();

    this.isLoadingFirstOrders = false;
  }

  /**
   * Handle orders questions scroll.
   */
  handleOrdersScroll() {
    const element = this.$refs.ordersScrollEndElement || null;

    if (element) {
      this.scrollObserver = new IntersectionObserver(
        this.handleScrollEndReached
      );

      this.scrollObserver.observe(element);
    }
  }

  /**
   * Handle get user external User.
   */
  async handleGetServicePointUser() {
    try {
      const response = await usersService.getUserProzisPoints();
      const { result: prozisPoints = 0 } = response || {};

      this.assignedPoints = prozisPoints;
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleGetServicePointUser',
          scene: 'ServicePointHomeScene'
        });
      }
    }
  }

  /**
   * Handle get deliveries.
   */
  async handleGetDeliveries() {
    try {
      const deliveries = (await servicePointService.getDeliveries()) || [];
      const { PROCESSING } = orderState;

      this.deliveries = deliveries.map(
        ({
          client: { name: clientName = null },
          expectedDeliveryDate = null,
          orderCode,
          packages = [],
          trackingNumber
        }) =>
          ({
            deliveredTo: clientName,
            delivery: {
              client: { name: clientName },
              orderCode,
              trackingNumber
            },
            packages,
            status: {
              code: PROCESSING,
              name: this.i18n(`SERVICE_POINT.LABELS.${PROCESSING}`)
            },
            statusDate: expectedDeliveryDate
          } as ServicePointOrderModel)
      );
    } catch (error) {
      this.deliveries = [];

      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleGetDeliveries',
          scene: 'ServicePointHomeScene'
        });
      }
    }
  }

  /**
   * Handle scroll end reached.
   */
  handleScrollEndReached([entry]) {
    if (this.selectedTab === this.ordersTypes.ALL_ORDERS) {
      if (entry && entry.isIntersecting) {
        this.handleGetAllOrders();
      }
    }
  }

  /**
   * Handle get all orders.
   */
  async handleGetAllOrders() {
    try {
      this.isLoadingAllOrders = true;

      const chunkOfOrders =
        (await servicePointService.getOrders({
          limit: 10,
          page: this.allOrdersPage
        })) || [];

      this.allOrders = [
        ...(this.allOrdersPage > 1 ? this.allOrders || [] : []),
        ...(chunkOfOrders || [])
      ];

      if (chunkOfOrders.length) {
        this.allOrdersPage++;
      }
    } catch (error) {
      this.allOrders = [];

      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleGetAllOrders',
          scene: 'ServicePointHomeScene'
        });
      }
    } finally {
      this.isLoadingAllOrders = false;
    }
  }

  /**
   * Handle get pending orders.
   */
  async handleGetPendingOrders() {
    try {
      this.isLoadingPendingOrders = true;

      this.pendingOrders =
        (await servicePointService.getOrders({
          status: `${orderState.RECEIVED},${orderState.TO_COLLECT}`
        })) || [];
    } catch (error) {
      this.pendingOrders = [];

      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleGetPendingOrders',
          scene: 'ServicePointHomeScene'
        });
      }
    } finally {
      this.isLoadingPendingOrders = false;
    }
  }

  /**
   * Handle get orders in transit.
   */
  async handleGetOrdersInTransit() {
    try {
      this.isLoadingPendingOrders = true;

      this.ordersInTransit =
        (await servicePointService.getOrders({
          status: orderState.RECEIVE_PENDING_TO_COLLECT
        })) || [];
    } catch (error) {
      this.ordersInTransit = [];

      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleGetOrdersIntransit',
          scene: 'ServicePointHomeScene'
        });
      }
    } finally {
      this.isLoadingPendingOrders = false;
    }
  }

  /**
   * Handle get orders totals.
   */
  async handleGetOrdersTotals() {
    try {
      this.ordersTotals = await servicePointService.getOrdersTotals();
    } catch (error) {
      if (process.env.BUILD_MODE !== 'prd') {
        console.error({
          error,
          method: 'handleGetOrdersTotals',
          scene: 'ServicePointHomeScene'
        });
      }
    }
  }

  /**
   * Handle view pending orders.
   */
  async handleViewPendingOrders() {
    this.selectedTab = this.ordersTypes.PENDING_ORDERS;

    await this.handleGetPendingOrders();
  }

  /**
   * Handle view orders in transit.
   */
  async handleViewOrdersInTransit() {
    this.selectedTab = this.ordersTypes.IN_TRANSIT;

    await this.handleGetOrdersInTransit();
  }

  /**
   * Handle view all orders.
   */
  async handleViewAllOrders() {
    this.selectedTab = this.ordersTypes.ALL_ORDERS;
    this.allOrdersPage = 1;

    await this.handleGetAllOrders();

    if (!this.scrollObserver) {
      this.handleOrdersScroll();
    }
  }

  /**
   * Handle sheet swipe.
   */
  handleSheetSwipe(direction) {
    switch (direction) {
      case 'up':
        this.isBottomSheetExpanded = true;
        break;

      case 'down':
        this.isBottomSheetExpanded = false;
        break;
    }
  }

  /**
   * Handle receive packages.
   */
  handleReceivePackages() {
    this.$router.push({
      name: 'ServicePointReceivePackagesScan'
    });
  }

  /**
   * Handle deliver order.
   */
  handleDeliverOrder() {
    const orders = [
      ...(this.deliveries || []),
      ...(this.pendingOrders || []),
      ...(this.ordersInTransit || [])
    ];

    servicePointStore.setOrders(orders);
    servicePointStore.setDeliverTrackingNumberKey(null);

    this.$router.push({
      name: 'ServicePointDeliverOrderScan'
    });
  }

  /**
   * Handle returns.
   */
  handleReturns() {
    const orders = [
      ...(this.deliveries || []),
      ...(this.pendingOrders || []),
      ...(this.ordersInTransit || [])
    ];

    servicePointStore.setOrders(orders);

    this.$router.push({
      name: 'ServicePointCollectOrderScan'
    });
  }

  /**
   * Handle sheet expanded.
   */
  @Watch('isBottomSheetExpanded')
  handleSheetExpanded() {
    if (!this.scrollObserver) {
      this.handleOrdersScroll();
    }

    this.$nextTick(() => {
      const { innerHeight: windowHeight } = window;

      if (windowHeight) {
        this.ordersHeight = windowHeight - windowHeight / 2 - 32;
      }
    });
  }
}
